import type {EligibilityReason} from './controlState';

export interface TaskEligibilityInput {
	readOnly: {isReadOnly: boolean} | null;
	// From Util.isClientTaskActionsRestricted(viewer) — real, already computed at every one of
	// this control's mount points (Draggable_card.js, Task_row.js, TaskRow.js) for their own
	// disabled/locked logic; this surfaces it as a named, typed reason instead of a blunt disable.
	isClientActionsRestricted: boolean;
}

// task_locked and role are both derivable today. no_agent_for_role / no_genai_access need
// permission data no surface currently fetches — they stay unreachable until a later ticket
// wires them.
//
// Fixed order, not a scoring system — kept as a literal list so it stays legible. Only
// task_locked and role can actually fire today; the other two are listed for completeness of
// the order, not because either branch can currently produce them.
const PRIORITY: EligibilityReason[] = ['task_locked', 'role', 'no_agent_for_role', 'no_genai_access'];

export function deriveEligibilityFromTask(task: TaskEligibilityInput): EligibilityReason | null {
	const applicable: Partial<Record<EligibilityReason, boolean>> = {
		task_locked: !!task.readOnly?.isReadOnly,
		role: task.isClientActionsRestricted,
		no_agent_for_role: false,
		no_genai_access: false,
	};
	return PRIORITY.find(reason => applicable[reason]) ?? null;
}

// A role-restricted viewer's control is hidden per-task (see 'role' above), which can leave a
// table's Nova column entirely empty. Rather than show a pointless empty column, the column
// itself hides for such a viewer — except a locked task still shows its control (task_locked
// wins over role), so the column stays visible whenever at least one visible task is locked.
// Callers own how "any visible task is locked" is computed (flat list vs. tree), since that
// varies by table implementation.
export function shouldShowNovaColumn(input: {isClientActionsRestricted: boolean; anyVisibleTaskLocked: boolean}): boolean {
	if (!input.isClientActionsRestricted) {
		return true;
	}
	return input.anyVisibleTaskLocked;
}
